<template>
  <div id="app-wrapper">
    <!-- Background tetap + dekorasi aset PP26 -->  
    <div class="fixed-background">
      <img class="deco deco--01" :src="require('@/assets/pcu/2026/Asset PP26-01.png')" aria-hidden="true">
      <img class="deco deco--02" :src="require('@/assets/pcu/2026/Asset PP26-02.png')" aria-hidden="true">
      <img class="deco deco--03" :src="require('@/assets/pcu/2026/Asset PP26-03.png')" aria-hidden="true">
      <img class="deco deco--04" :src="require('@/assets/pcu/2026/Asset PP26-04.png')" aria-hidden="true">
      <img class="deco deco--05" :src="require('@/assets/pcu/2026/Asset PP26-05.png')" aria-hidden="true">
      <img class="deco deco--06" :src="require('@/assets/pcu/2026/Asset PP26-06.png')" aria-hidden="true">
      <img class="deco deco--07" :src="require('@/assets/pcu/2026/Asset PP26-07.png')" aria-hidden="true">
      <img class="deco deco--08" :src="require('@/assets/pcu/2026/Asset PP26-08.png')" aria-hidden="true">
      <img class="deco deco--09" :src="require('@/assets/pcu/2026/Asset PP26-09.png')" aria-hidden="true">
    </div>
    <div class="content-scroll-wrapper">
      <ToastContainer /> 
      <MainLayout :app-state="appState">
        <template #header><AppHeader /></template>
        
        <template #main>
          <Transition name="slide-fade" mode="out-in">
            
            <div v-if="appState === 'LOADING' || appState === 'CALCULATING'" class="status-container" key="loading">
              <LoadingSpinner />
              <p v-if="appState === 'CALCULATING'" style="margin-top: 1rem;">Menganalisa jawabanmu...</p>
            </div>
    
            <div v-else-if="appState === 'ERROR'" class="status-container error" key="error">
              <h2>Oops! Terjadi Kesalahan</h2>
              <p>{{ error }}</p>
              <button @click="fetchInitialData" class="retry-button">Coba Lagi</button>
            </div>
    
            <WelcomeCard 
              v-else-if="appState === 'WELCOME'"
              v-model:formData="userData"
              :schoolList="schoolList"
              :allow-new-school="false"
              :show-grade="false"
              :show-paralel="false"
              @start="handleQuizStart"
              @resume-student="handleResumeStudent"
              key="welcome"
            />

            <NicknameCard
              v-else-if="appState === 'NICKNAME'"
              :user-name="userData.name"
              :initial-nickname="nickname"
              @next="handleNicknameNext"
              @back="handleNicknameBack"
              key="nickname"
            />

            <ProdiSelector
              v-else-if="appState === 'PRODI_SELECT'"
              :prodis="prodiList"
              :is-loading="isProdiLoading"
              :load-error="prodiLoadError"
              :nickname="nickname"
              :initial-selected-ids="selectedProdis"
              @next="handleProdiNext"
              @skip="handleProdiSkip"
              @back="handleProdiBack"
              key="prodi-select"
            />

            <ExtraProdiInput
              v-else-if="appState === 'EXTRA_PRODI'"
              :prodis="prodiList"
              :nickname="nickname"
              :initial-items="extraProdis"
              @next="handleExtraProdiNext"
              @back="handleExtraProdiBack"
              key="extra-prodi"
            />

            <TestChoiceCard
              v-else-if="appState === 'TEST_CHOICE'"
              :nickname="nickname"
              :selected-prodis="selectedProdis"
              :extra-prodis="extraProdis"
              @take="handleTestChoiceTake"
              @skip="handleTestChoiceSkip"
              @back="handleTestChoiceBack"
              key="test-choice"
            />

            <CloseCard
              v-else-if="appState === 'CLOSE'"
            />
    
            <div v-else-if="appState === 'QUIZ' && currentQuestion" class="quiz-container" :key="currentQuestion.id">
              
              <div class="progress-header">
                <h1 class="main-title">Pertanyaan {{ currentQuestionIndex + 1 }} dari {{ questions.length }}</h1>
                <div class="progress-bar-container"><div class="progress-bar-fill" :style="{ width: progressPercentage }"></div></div>
              </div>
              
              <QuizCard>
                <CategorySorter 
                  v-if="currentQuestion.type === 'sorter'" 
                  v-model="rankedCategories" 
                  :categories="sortingCategories" 
                />
    
                <ChoiceQuestion 
                  v-else-if="currentQuestion.type === 'multi_choice'" 
                  v-model="multiChoiceSelection"
                  :title="currentQuestion.content.title"
                  :subtitle="currentQuestion.content.subtitle"
                  :choices="currentQuestion.content.choices"
                  :max-selections="currentQuestion.content.maxSelections"
                />
                
                <ChoiceQuestion 
                  v-else-if="currentQuestion.type === 'final_prodi_choice'" 
                  v-model="finalProdiChoice"
                  :title="currentQuestion.content.title"
                  :subtitle="currentQuestion.content.subtitle"
                  :choices="currentQuestion.content.choices"
                  :max-selections="currentQuestion.content.maxSelections"
                  :card-view="true"
                />
                
                <RelatabilitySlider 
                  v-else 
                  :id="currentQuestion.id" 
                  :question="currentQuestion.question" 
                  :type="currentQuestion.type" 
                  :content="currentQuestion.content"
                  :filename="currentQuestion.filename"
                  :initial-value="currentQuestion.userValue" 
                  @update:value="updateAnswer"
                />
    
              </QuizCard>
    
              <div class="navigation-buttons">
                <button @click="handleBackClick" class="nav-button prev-button">Back</button>
                <button @click="handleNextClick" class="nav-button" :class="nextButtonClass">
                  {{ nextButtonText }}
                </button>
              </div>
            </div>
    
            <ThanksCard v-else-if="appState === 'THANKS'" :nickname="nickname" />
            <BarcodeCard
              v-else-if="appState === 'BARCODE'"
              :nickname="nickname"
              :full-name="userData.name"
              :barcode-code="barcodeCode"
              :results="finalRecommendations"
              :selected-prodis="selectedProdis"
              :extra-prodis="extraProdis"
              :prodi-list="prodiList"
              :chosen-prodi-name="chosenProdiName"
            />
            <ResultsDisplay
              v-else-if="appState === 'RESULTS'"
              :results="finalRecommendations"
              :selected-prodis="selectedProdis"
              :extra-prodis="extraProdis"
              :prodi-list="prodiList"
              :nickname="nickname"
              :is-resume-mode="resumeMode"
              @finish="showThanksScreen"
            />
          </Transition>
        </template>
        <template #footer><AppFooter /></template>
      </MainLayout>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, reactive } from 'vue';

// --- SAFARI DETECTION ---
function isSafari() {
  const ua = window.navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS|Android/.test(ua);
}
import MainLayout from '../layouts/MainLayout.vue';
import AppHeader from '../components/AppHeader.vue';
import AppFooter from '../components/AppFooter.vue';
import QuizCard from '../components/QuizCard.vue';
import RelatabilitySlider from '../components/RelatabilitySlider.vue';
import LoadingSpinner from '../components/LoadingSpinner.vue';
import WelcomeCard from '../components/WelcomeCard.vue';
import CloseCard from '../components/CloseCard.vue';
import CategorySorter from '../components/CategorySorter.vue';
import ChoiceQuestion from '../components/ChoiceQuestion.vue';
import ThanksCard from '../components/ThanksCard.vue';
import ResultsDisplay from '../components/ResultsDisplay.vue';
import ProdiSelector from '../components/ProdiSelector.vue';
import NicknameCard from '../components/NicknameCard.vue';
import ExtraProdiInput from '../components/ExtraProdiInput.vue';
import TestChoiceCard from '../components/TestChoiceCard.vue';
import BarcodeCard from '../components/BarcodeCard.vue';
import ToastContainer from '../components/ToastContainer.vue'; // <-- Import kontainer
import { useToast } from '../composables/useToast'; // <-- Import "otak" notifikasi
// import ResultsCard from '../components/ResultsCard.vue';

// --- STATE MANAGEMENT ---
const appState = ref('LOADING'); // State: LOADING, WELCOME, QUIZ, CALCULATING, FINISHED, ERROR
const questions = ref([]);
const sortingCategories = ref(null);
const schoolList = ref([]);
const rankedCategories = ref([]);
const userData = reactive({ name: '', school: '', grade: '', email: '', paralel: '', phone: '', nickname: '' }); // Data user yang akan dikirim ke backend
const nickname = ref(''); // Nama panggilan user
const currentQuestionIndex = ref(0);
const error = ref(null);
const multiChoiceSelection = ref([]); // Menyimpan 2 pilihan user
const finalProdiChoice = ref([]);   // Menyimpan pilihan prodi akhir (array kode)
const finalRecommendations = ref(null);
const prodiList = ref({});
const selectedProdis = ref([]);
const extraProdis = ref([]);
const isProdiLoading = ref(false);
const prodiLoadError = ref(null);
const barcodeCode = ref('');
const chosenProdiName = ref('');
const resumeMode = ref(false); // true saat user masuk via cek hasil dan belum pilih jurusan kunjungan
const { addToast } = useToast(); // Siapkan fungsi addToast

// --- API & DATA FUNCTIONS ---

// Helper function untuk POST request yang lebih rapi
async function postToApi(endpoint, payload) {
  const baseUrl = process.env.VUE_APP_API_URL; // Sesuaikan base URL API Anda
  const apiUrl = `${baseUrl}${endpoint}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error(`API Error on ${endpoint}. Status: ${response.status}`);
  return response.json();
}

async function getFinalRecommendations() {
  if (!finalProdiChoice.value || finalProdiChoice.value.length === 0) {
    addToast('Pilih minimal 1 pernyataan prodi terlebih dahulu.', 'error');
    return;
  }
  // appState.value = 'CALCULATING_RESULTS';
  appState.value = 'LOADING';
  
  // Helper function untuk mengubah nilai 0-100 menjadi 1-4
  const scoreMap = val => val <= 20 ? 1 : (val <= 49 ? 2 : (val <= 80 ? 3 : 4));

  const payload = {
    user_data: userData,
    selected_prodis: selectedProdis.value,
    extra_prodis: extraProdis.value,
    // Gunakan scoreMap untuk mengubah nilai 'userValue' menjadi skala 1-4
    slider_answers: questions.value
      .filter(q => q.type !== 'sorter' && q.type !== 'multi_choice' && q.type !== 'final_prodi_choice')
      .map(q => ({ 
        id: q.id, 
        value: scoreMap(q.userValue), // <-- Perubahan terjadi di sini
        category: q.category 
      })),
    category_ranking: rankedCategories.value.map(item => item.code),
    multi_choice: multiChoiceSelection.value,
    final_choice: finalProdiChoice.value.map(kode => {
      const c = currentQuestion.value?.content?.choices?.find(x => x.kode === kode);
      return c ? { kode: c.kode, prodi_id: c.prodi_id } : null;
    }).filter(Boolean),
    skipped_test: false
  };

  // console.log('Mengirim payload final ke backend:', payload);

  try {
    // Kirim data ke endpoint baru di backend Anda
    const responseData = await postToApi('/getprogram', payload);
    finalRecommendations.value = responseData.data;
    appState.value = 'RESULTS'; // Tampilkan halaman rekomendasi
    // appState.value = 'THANKS'; // Tampilkan halaman rekomendasi
  } catch (err) {
    appState.value = 'QUIZ'; // Kembali ke pertanyaan terakhir
    addToast('Gagal mendapatkan rekomendasi. Periksa koneksi dan coba lagi.', 'error');
  }
}

async function fetchProdiList() {
  isProdiLoading.value = true;
  prodiLoadError.value = null;
  try {
    const apiUrl = `${process.env.VUE_APP_API_URL}/getProdi/1`;
    const response = await fetch(apiUrl, { headers: { 'Accept': 'application/json' } });
    if (!response.ok) throw new Error(`Status: ${response.status}`);
    const responseData = await response.json();
    prodiList.value = responseData.data.prodis || {};
  } catch (err) {
    prodiLoadError.value = 'Gagal memuat daftar prodi.';
  } finally {
    isProdiLoading.value = false;
  }
}

async function fetchInitialData() {
  const now = new Date();
  
  const openPeriods = [
    { start: new Date('2025-07-30T15:00:00+07:00'), end: new Date('2025-07-30T23:59:59+07:00') },
    { start: new Date('2025-07-31T15:00:00+07:00'), end: new Date('2025-07-31T23:59:59+07:00') },
    { start: new Date('2025-08-01T15:00:00+07:00'), end: new Date('2025-08-01T23:59:59+07:00') },
    { start: new Date('2025-08-02T00:00:00+07:00'), end: new Date('2025-08-02T23:59:59+07:00') },
  ];

  const isOpen = openPeriods.some(period => now >= period.start && now <= period.end);
  
  // if (!isOpen) {
  if (isOpen) { // [TESTING] Aktifkan akses kapan saja selama pengembangan
    appState.value = 'CLOSE';
  } else {
    appState.value = 'LOADING';
    error.value = null;
    try {
      const apiUrl = `${process.env.VUE_APP_API_URL}/questionminat`; // Endpoint GET awal
      if (!apiUrl) throw new Error('Variabel .env belum di-set');
      const response = await fetch(apiUrl, { headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Gagal mengambil data. Status: ${response.status}`);
      const responseData = await response.json();
      questions.value = responseData.data.questions.map(q => ({ ...q, userValue: 50, category: q.category }));
      sortingCategories.value = responseData.data.sorting_categorys;
      schoolList.value = Object.values(responseData.data.schools);
      // console.log('schoolList:', schoolList.value);
      questions.value.push({ id: 999, type: 'sorter', question: 'Urutkan Preferensi Kamu' });

      // tambah 2 pertanyaan kosong untuk question multiple choice dan final prodi choice
      questions.value.push({});
      questions.value.push({});

      appState.value = 'WELCOME';
      // Ambil daftar prodi di background
      fetchProdiList();
    } catch (err) { handleError(err, 'Gagal mengambil data awal.'); }
  }
}

// ---- Back button / browser navigation guard ----
const UNGUARDED_STATES = ['LOADING', 'WELCOME', 'CLOSE', 'ERROR', 'THANKS', 'BARCODE'];

function handleBeforeUnload(e) {
  if (UNGUARDED_STATES.includes(appState.value)) return;
  e.preventDefault();
  e.returnValue = '';
}

function handlePopState() {
  if (UNGUARDED_STATES.includes(appState.value)) {
    // No guard needed — let it navigate away naturally
    return;
  }
  // Hindari pushState loop di Safari
  if (!isSafari()) {
    history.pushState(null, '', window.location.href);
  }
  const confirmed = window.confirm('Apakah kamu yakin ingin keluar? Progress kamu akan hilang.');
  if (confirmed) {
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    history.back();
  }
}

onMounted(() => {
  // Hindari pushState loop di Safari
  if (!isSafari()) {
    history.pushState(null, '', window.location.href);
  }
  window.addEventListener('popstate', handlePopState);
  window.addEventListener('beforeunload', handleBeforeUnload);
  fetchInitialData();
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handlePopState);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

// --- CORE LOGIC FUNCTIONS ---

// Dipanggil setelah halaman sorting diselesaikan
async function getMultiChoiceQuestion() {
  appState.value = 'CALCULATING';

  // hapus 2 pertanyaan dari antrian
  questions.value.pop();
  questions.value.pop(); 
  
  const categoryScores = { R: 0, I: 0, A: 0, S: 0, E: 0, C: 0 };
  const scoreMap = val => val <= 20 ? 1 : (val <= 49 ? 2 : (val <= 80 ? 3 : 4));
  questions.value.filter(q => q.type !== 'sorter').forEach(q => {
    if (Object.prototype.hasOwnProperty.call(categoryScores, q.category)) {
      categoryScores[q.category] += scoreMap(q.userValue);
    }
  });

  const payload = {
    scores: categoryScores,
    sorter_category: rankedCategories.value.map(item => item.code)
  };
  
  try {
    // API call pertama untuk mendapatkan pilihan ganda (pilih 2)
    const responseData = await postToApi('/questioncombination', payload);
    questions.value.push({
      id: 1001, type: 'multi_choice', question: 'Pilih 2 Pernyataan',
      content: {
        title: "Dua Minat Teratas",
        subtitle: "Dari semua jawabanmu, kami menemukan beberapa area minat yang menonjol. <b>Pilih dua</b> yang paling kamu banget!",
        choices: responseData.data.question,
        maxSelections: 2,
      }
    });
    currentQuestionIndex.value++;

    // tambah pertanyaan kosong untuk final prodi choice
    questions.value.push({});

    appState.value = 'QUIZ';
  } catch (err) {
    // Kembalikan pertanyaan yang sudah di-pop dan kembali ke QUIZ
    questions.value.push({});
    questions.value.push({});
    appState.value = 'QUIZ';
    addToast('Gagal memproses hasil. Periksa koneksi dan coba lagi.', 'error');
  }
}

// Dipanggil setelah memilih 2 dari pilihan ganda
async function getProdiChoiceQuestion() {
  if (multiChoiceSelection.value.length !== 2) {
    addToast(`Kamu harus memilih tepat 2 pilihan.`, "error"); // <-- Ganti dengan ini
    return;
  }
  appState.value = 'CALCULATING';

  // hapus 1 pertanyaan dari antrian
  questions.value.pop();

  const payload = { choices: multiChoiceSelection.value };
  
  try {
    // API call kedua untuk mendapatkan list pernyataan prodi
    const responseData = await postToApi('/questionchoices', payload);
    questions.value.push({
      id: 1002, type: 'final_prodi_choice', question: 'Pilih Pernyataan Prodi',
      content: {
        title: "2 yang Paling Kamu Minati",
        subtitle: "<b>Pilih maksimal dua (2)</b> yang paling cocok dengan dirimu!",
        choices: responseData.data.question,
        maxSelections: 2,
      }
    });
    currentQuestionIndex.value++;
    appState.value = 'QUIZ';
  } catch (err) {
    // Kembalikan pertanyaan yang sudah di-pop dan kembali ke QUIZ
    questions.value.push({});
    multiChoiceSelection.value = [];
    appState.value = 'QUIZ';
    addToast('Gagal memuat pertanyaan prodi. Periksa koneksi dan coba lagi.', 'error');
  }
}

function handleError(err, defaultMessage = 'Terjadi kesalahan.') {
  // console.error(defaultMessage, err);
  error.value = err.message || defaultMessage;
  appState.value = 'ERROR';
}

// --- NAVIGATION & EVENT HANDLERS ---
function handleNextClick() {
  const type = currentQuestion.value?.type;
  
  if (type === 'sorter') {
    getMultiChoiceQuestion();
  } else if (type === 'multi_choice') {
    getProdiChoiceQuestion();
  } else if (type === 'final_prodi_choice') {
    // Ini adalah perubahan penting. Di pertanyaan terakhir, panggil API rekomendasi.
    getFinalRecommendations();
  } else {
    // Jika masih ada pertanyaan slider, lanjutkan
    if (!isLastQuestion.value) currentQuestionIndex.value++;
  }
}

function showThanksScreen(data) {
  if (data?.barcodeCode) {
    barcodeCode.value = data.barcodeCode;
    chosenProdiName.value = data.prodiName || '';
    appState.value = 'BARCODE';
  } else {
    appState.value = 'THANKS';
  }
}

async function handleResumeStudent(contactData) {
  appState.value = 'LOADING';
  try {
    const responseData = await postToApi('/getActiveStudent', {
      email: contactData.email,
      phone: contactData.phone
    });
    const data = responseData.data;
    barcodeCode.value = data.student?.barcode || '';
    nickname.value = data.student?.nickname || data.student?.name?.split(' ')[0] || '';
    finalRecommendations.value = { priority_program: data.student?.priority_program || '' };
    chosenProdiName.value = data.pilihan_kunjungi || '';
    selectedProdis.value = Object.keys(data.pilihan_program_petra || {});
    extraProdis.value = Object.keys(data.pilihan_program_luar || {});
    // Gabungkan nama prodi dari respons API ke prodiList agar nama tampil
    // meskipun fetchProdiList() belum selesai (misalnya koneksi lambat di mobile)
    prodiList.value = {
      ...prodiList.value,
      ...(data.pilihan_program_petra || {}),
      ...(data.pilihan_program_luar || {})
    };
    // Pastikan daftar lengkap juga dimuat di background jika belum ada
    if (Object.keys(prodiList.value).length === 0) {
      fetchProdiList();
    }
    // Cek apakah pilihan_akhir (pilihan program kunjungan) sudah terisi
    if (data.pilihan_kunjungi) {
      // Sudah memilih program — langsung ke halaman barcode
      finalRecommendations.value = { priority_program: data.student?.priority_program || '' };
      resumeMode.value = false;
      appState.value = 'BARCODE';
    } else {
      // Belum memilih — arahkan ke ResultsDisplay untuk mengisi pilihan program kunjungan
      finalRecommendations.value = {
        priority_program: data.student?.priority_program || '',
        choice_program: data.choice_program || [],
        student: data.student || {}
      };
      resumeMode.value = true;
      appState.value = 'RESULTS';
    }
  } catch (err) {
    error.value = 'Gagal mengambil data siswa. Periksa koneksi dan coba lagi.';
    appState.value = 'ERROR';
  }
}

function handleBackClick() {
  const currentQType = currentQuestion.value?.type;

  // Jika kita berada di pertanyaan dinamis (multi_choice atau final_prodi_choice)
  if (currentQType === 'multi_choice' || currentQType === 'final_prodi_choice') {
    // Reset state pilihan yang relevan
    if (currentQType === 'multi_choice') multiChoiceSelection.value = [];
    if (currentQType === 'final_prodi_choice') finalProdiChoice.value = [];
    // Pindahkan index ke pertanyaan sebelumnya
    currentQuestionIndex.value--;
  } 
  // Jika kita berada di pertanyaan pertama kuis
  else if (currentQuestionIndex.value === 0) {
    appState.value = 'TEST_CHOICE';
  } 
  // Untuk semua kasus lain (slider biasa)
  else {
    currentQuestionIndex.value--;
  }
}

function handleQuizStart(formData) {
  Object.assign(userData, formData);
  appState.value = 'NICKNAME';
}

function handleNicknameNext(chosenNickname) {
  nickname.value = chosenNickname;
  userData.nickname = chosenNickname;
  appState.value = 'PRODI_SELECT';
}

function handleNicknameBack() {
  appState.value = 'WELCOME';
}

function handleProdiNext(chosenIds) {
  selectedProdis.value = chosenIds;
  appState.value = 'EXTRA_PRODI';
}

function handleProdiSkip() {
  selectedProdis.value = [];
  appState.value = 'EXTRA_PRODI';
}

function handleProdiBack() {
  appState.value = 'NICKNAME';
}

function handleExtraProdiNext(extras) {
  extraProdis.value = extras;
  appState.value = 'TEST_CHOICE';
}

function handleExtraProdiBack() {
  appState.value = 'PRODI_SELECT';
}

function handleTestChoiceTake() {
  appState.value = 'QUIZ';
}

async function handleTestChoiceSkip() {
  appState.value = 'LOADING';
  const payload = {
    user_data: userData,
    selected_prodis: selectedProdis.value,
    extra_prodis: extraProdis.value,
    skipped_test: true,
    slider_answers: [],
    category_ranking: [],
    multi_choice: [],
    final_choice: []
  };
  try {
    const responseData = await postToApi('/getprogram', payload);
    finalRecommendations.value = responseData.data;
    appState.value = 'RESULTS';
  } catch (err) {
    appState.value = 'TEST_CHOICE'; // Kembali ke halaman pilihan test
    addToast('Gagal memproses data. Periksa koneksi dan coba lagi.', 'error');
  }
}

function handleTestChoiceBack() {
  appState.value = 'EXTRA_PRODI';
}

function updateAnswer(payload) {
  const questionToUpdate = questions.value.find(q => q.id === payload.id);
  if (questionToUpdate) questionToUpdate.userValue = payload.value;
}


// --- COMPUTED PROPERTIES ---
const totalQuestions = computed(() => questions.value.length);
const currentQuestion = computed(() => totalQuestions.value > 0 ? questions.value[currentQuestionIndex.value] : null);
const isLastQuestion = computed(() => currentQuestionIndex.value === totalQuestions.value - 1);

const progressPercentage = computed(() => {
  if (totalQuestions.value === 0) {
    return '0%';
  }
  // Kalkulasi persentase yang benar
  const percentage = ((currentQuestionIndex.value + 1) / (totalQuestions.value)) * 100;
  return `${percentage}%`;
});

const nextButtonText = computed(() => {
  const type = currentQuestion.value?.type;
  if (type === 'sorter' || type === 'multi_choice') return 'Next';
  // if (isLastQuestion.value) return 'Finish';
  if (isLastQuestion.value) return 'Next';
  return 'Next';
});

const nextButtonClass = computed(() => isLastQuestion.value ? 'finish-button' : 'next-button');
</script>